import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { CBMError, Siterecord, SiterecordCharacteristics } from 'app/models';
import { ComputationMethodType, EquipmentTypeWithoutVRI, SideType } from 'app/models/enums';
import { AuthorizationService, SiterecordService } from 'app/services';
import { CbmInputStyle } from 'app/shared/input/cbm-input-style';
import { MessageboxComponent } from 'app/shared/messagebox/messagebox.component';
import { enumValues } from 'app/shared/util/enumValues';
import { environment } from 'environments/environment';

@Component({
  selector: 'point-details-general',
  templateUrl: './point-details-general.component.html',
  styleUrls: ['./point-details.component.scss']
})
export class PointDetailsGeneralComponent implements OnInit, OnChanges {
  @Input() id: string;
  @Input() readonly: boolean;
  @Input() inShapeFileReadonly: boolean;
  @Input() inputStyle: CbmInputStyle;
  @Input() location: Siterecord;
  @Input() isRecordIdEditable: boolean;
  @Input() isCoordinatesEditable: boolean;
  @Input() routingPoints: string;

  @Output() notifyCancel: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() recordIdEditWarning: EventEmitter<boolean> = new EventEmitter();
  @Output() coordinatesEditWarning: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(MessageboxComponent) messagebox: MessageboxComponent;
  @ViewChild('pointDetailsGeneralForm') public form: NgForm = null;

  public errorMessages: CBMError[];
  public originalLocation: Siterecord;
  public recordIdFeatureEnabled = environment.recordIdFeatureEnabled;
  public tooltipTranslation: string;

  public computationMethods: string[];
  public equipmentTypesWithoutVRI: string[];
  public sideTypes: string[];

  public latitude: string;
  public longitude: string;
  public initialRecordId: string;

  public twoColumnStyle: CbmInputStyle = { label: 'col-xs-4', control: 'col-xs-6' };
  public threeColumnStyle: CbmInputStyle = { label: 'col-xs-4', control: 'col-xs-4' };
  public fourColumnStyle: CbmInputStyle = { label: 'col-xs-4', control: 'col-xs-3' };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly siterecordService: SiterecordService,
    private readonly authorizationService: AuthorizationService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.initialiseEnums();
    this.initializeTranslations();
    this.originalLocation = this.location;
    this.initialRecordId = this.location.recordId;
    this.sortCharacteristics();
    if (this.location.siterecordIntersections) {
      this.sortIntersections();
    }
  }

  ngOnChanges(): void {
    this.setCoordinates();
  }

  private setCoordinates(): void {
    const coordinates = this.location.routingPoints.split('|');
    const startCoordinates = coordinates[0].split('\\,');
    this.latitude = startCoordinates[1];
    this.longitude = startCoordinates[0];
  }

  private initialiseEnums(): void {
    this.computationMethods = enumValues(ComputationMethodType);
    this.equipmentTypesWithoutVRI = enumValues(EquipmentTypeWithoutVRI);
    this.sideTypes = enumValues(SideType);
  }

  private sortCharacteristics(): void {
    this.location.siterecordCharacteristics.sort((characteristicA, characteristicB) => {
      const orderA = characteristicA.characteristicsOrder;
      const orderB = characteristicB.characteristicsOrder;
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    });
  }

  private sortIntersections(): void {
    this.location.siterecordIntersections.sort((intersectionA, intersectionB) => {
      const orderA = intersectionA.intersectionsOrder;
      const orderB = intersectionB.intersectionsOrder;
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    });
  }

  protected initializeTranslations(): void {
    this.translateService
      .get('SITERECORD.RECORD_ID.UNSAVED_CHANGES_WARNING')
      .subscribe(translation => (this.tooltipTranslation = translation));
  }

  public onCancel() {
    this.errorMessages = [];

    this.readonly = !this.readonly;
    this.location = this.originalLocation;
    this.recordIdEditWarning.emit(false);
    this.notifyCancel.emit(this.readonly);
  }

  public onSubmit(): void {
    this.errorMessages = [];

    this.siterecordService.updateSiterecord(this.location).subscribe(
      response => {
        if (response.ok) {
          if (this.authorizationService.isLocationAdmin()) {
            this.router.navigate([`/admin/locations`]);
          } else {
            this.router.navigate([`/locations/${this.location.contractId}`]);
          }
        }
      },
      (errors: CBMError[]) => {
        this.errorMessages = errors;
      }
    );
  }

  public isEquipmentTypeVRI() {
    return this.location.equipmentTypeUsed === 'vri';
  }

  public addLane() {
    if (!this.location.siterecordCharacteristics) {
      this.location.siterecordCharacteristics = [];
    }

    const characteristics = new SiterecordCharacteristics();
    characteristics.characteristicsOrder = this.location.siterecordCharacteristics.length + 1;
    this.location.siterecordCharacteristics.push(characteristics);

    this.setNumberOfLanes();
  }

  public removeLane(): void {
    this.location.siterecordCharacteristics.pop();
    if (this.location.siterecordCharacteristics.length === 0) {
      this.location.siterecordCharacteristics = null;
    }

    this.setNumberOfLanes();
  }

  public setNumberOfLanes() {
    if (this.location.siterecordCharacteristics) {
      const specificLanes = this.location.siterecordCharacteristics.map(data => data.specificLane);
      const uniqueLanes = specificLanes.filter((x, i, a) => x && a.indexOf(x) === i);

      this.location.numberOfLanes = uniqueLanes.length;
    } else {
      this.location.numberOfLanes = 0;
    }
  }

  public getDisabledLanes(selectedLane: string): string[] {
    const disabledLanes: string[] = [];
    let selectedLanes: string[] = [];
    this.location.siterecordCharacteristics.map(sc => {
      if (sc.specificLane) {
        selectedLanes.push(sc.specificLane);
      }
    });

    selectedLanes = selectedLanes.sort();
    for (let i = 0; i < selectedLanes.length - 1; i++) {
      if (selectedLanes[i + 1] === selectedLanes[i]) {
        disabledLanes.push(selectedLanes[i]);
      }
    }
    return disabledLanes;
  }

  public getDisabledMeasurementValuetypes(characteristics: SiterecordCharacteristics) {
    const disabledMeasurementValuetypes: string[] = [];
    const selectedLane = characteristics.specificLane;

    this.location.siterecordCharacteristics.map(sc => {
      if (sc.specificLane && sc.specificLane === selectedLane) {
        disabledMeasurementValuetypes.push(sc.measurementValueType);
      }
    });

    return disabledMeasurementValuetypes;
  }

  public triggerRecordIdEditEvent() {
    this.recordIdEditWarning.emit(true);
  }

  public triggerCoordinatesEditEvent() {
    this.coordinatesEditWarning.emit(true);
  }

  protected isLocationAdmin(): boolean {
    return this.authorizationService.isLocationAdmin();
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/point-details/general/point-details-general.component.ts